import type { FluxErrorEntityErrorCodes } from '../types.js';

export const PipeErrorCodes = {
  PipeAlreadyHasDeadline: {
    code: 'pipe/already-has-deadline',
    explanation: 'Este fluxo (pipe) já possui um prazo definido.',
    localizations: {},
    message: 'Este fluxo já possui um prazo definido.',
  },
  PipeCannotRemoveAssignedMembers: {
    code: 'pipe/cannot-remove-assigned-members',
    explanation:
      'Você não pode remover membros de um fluxo (pipe) que estão atribuídos a uma automação.',
    localizations: {},
    message: 'Não é possível remover membros que estão atribuídos a uma automação.',
  },
  PipeCreate: {
    code: 'pipe/create',
    explanation:
      'Ocorreu um erro desconhecido ao tentar criar um novo fluxo (pipe). Verifique suas informações e tente novamente.',
    localizations: {},
    message: 'Erro ao criar este fluxo.',
  },
  PipeDelete: {
    code: 'pipe/delete',
    explanation: 'Ocorreu um erro ao tentar excluir este fluxo (pipe).',
    localizations: {},
    message: 'Erro ao excluir este fluxo. Tente novamente.',
  },
  PipeDoesNotHaveDeadline: {
    code: 'pipe/does-not-have-deadline',
    explanation: 'Este fluxo (pipe) não possui um prazo definido.',
    localizations: {},
    message: 'Este fluxo não possui um prazo definido.',
    supportingDocs: [
      {
        name: 'Configuração do Fluxo',
        path: '/wiki/docs/pipe/configuration#prazos',
      },
    ],
  },
  PipeMemberNotFound: {
    code: 'pipe/member-not-found',
    explanation: 'O membro do fluxo (pipe) que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'Membro do fluxo não encontrado.',
  },
  PipeNoStartForm: {
    code: 'pipe/no-start-form',
    explanation:
      'Este fluxo (pipe) não possui um formulário inicial configurado. O formulário inicial é necessário para criar cards neste fluxo.',
    localizations: {},
    message: 'Este fluxo não possui um formulário inicial configurado.',
    supportingDocs: [
      {
        name: 'Configuração do Formulário Inicial',
        path: '/wiki/docs/pipe/initial-form',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  PipeNotFound: {
    code: 'pipe/not-found',
    explanation:
      'O fluxo (pipe) que você está tentando acessar não foi encontrado. Isso pode ocorrer se o fluxo não existir ou se você não tiver permissão para acessá-lo.',
    localizations: {},
    message: 'O fluxo solicitado não foi encontrado. Ele pode ter sido arquivado ou removido.',
  },
  PipeNotStartForm: {
    code: 'pipe/not-start-form',
    explanation: 'Este fluxo (pipe) não possui um formulário inicial configurado.',
    localizations: {},
    message: 'Este fluxo não tem um formulário inicial configurado.',
    supportingDocs: [
      {
        name: 'Configuração do Formulário Inicial',
        path: '/wiki/docs/pipe/initial-form',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  PipeOwnershipCannotTransferToSelf: {
    code: 'pipe/ownership-cannot-transfer-to-self',
    explanation:
      'Você não pode transferir a propriedade de um fluxo (pipe) para si mesmo. Você deve selecionar outro usuário.',
    localizations: {},
    message: 'Não é possível transferir a propriedade para si mesmo.',
  },
  PipeUndelete: {
    code: 'pipe/undelete',
    explanation: 'Ocorreu um erro ao tentar restaurar este fluxo (pipe).',
    localizations: {},
    message: 'Erro ao restaurar este fluxo.',
  },
  PipeUpdate: {
    code: 'pipe/update',
    explanation:
      'Ocorreu um erro ao tentar atualizar este fluxo (pipe). Verifique suas informações e tente novamente.',
    localizations: {},
    message: 'Erro ao atualizar este fluxo.',
  },
  PipeUserNotMember: {
    code: 'pipe/user-not-member',
    explanation: 'O usuário que você está tentando acessar não é membro deste fluxo (pipe).',
    localizations: {},
    message: 'Você não tem acesso a este fluxo.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'pipe'>;

export const PipeIdLookupErrorCodes = {
  PipeIdLookupAttachmentId: {
    code: 'pipe-id-lookup/attachment-id',
    explanation:
      'O ID do anexo fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o anexo foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do anexo não foi encontrado.',
  },
  PipeIdLookupAutomationId: {
    code: 'pipe-id-lookup/automation-id',
    explanation:
      'O ID da automação fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a automação foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da automação não foi encontrado.',
  },
  PipeIdLookupCardCommentId: {
    code: 'pipe-id-lookup/card-comment-id',
    explanation:
      'O ID do comentário do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o comentário foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do comentário do card não foi encontrado.',
  },
  PipeIdLookupCardId: {
    code: 'pipe-id-lookup/card-id',
    explanation:
      'O ID do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o card foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do card não foi encontrado.',
  },
  PipeIdLookupCardTaskAssigneeId: {
    code: 'pipe-id-lookup/card-task-assignee-id',
    explanation:
      'O ID do responsável pela tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o responsável pela tarefa foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do responsável pela tarefa não foi encontrado.',
  },
  PipeIdLookupCardTaskFieldId: {
    code: 'pipe-id-lookup/card-task-field-id',
    explanation:
      'O ID do campo da tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o campo foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do campo da tarefa do card não foi encontrado.',
  },
  PipeIdLookupCardTaskId: {
    code: 'pipe-id-lookup/card-task-id',
    explanation:
      'O ID da tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a tarefa foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da tarefa do card não foi encontrado.',
  },
  PipeIdLookupFormAnswerQuestionId: {
    code: 'pipe-id-lookup/form-answer-question-id',
    explanation:
      'O ID da pergunta da resposta do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a pergunta foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da pergunta da resposta do formulário não foi encontrado.',
  },
  PipeIdLookupFormId: {
    code: 'pipe-id-lookup/form-id',
    explanation:
      'O ID do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o formulário foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do formulário não foi encontrado.',
  },
  PipeIdLookupFormQuestionId: {
    code: 'pipe-id-lookup/form-question-id',
    explanation:
      'O ID da pergunta do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a pergunta foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da pergunta do formulário não foi encontrado.',
  },
  PipeIdLookupLabelId: {
    code: 'pipe-id-lookup/label-id',
    explanation:
      'O ID da etiqueta fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a etiqueta foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da etiqueta não foi encontrado.',
  },
  PipeIdLookupReviewId: {
    code: 'pipe-id-lookup/review-id',
    explanation:
      'O ID da revisão é obrigatório para a pesquisa de ID de fluxo (pipe). Esse erro pode ocorrer se a revisão foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'ID da aprovação é obrigatório.',
  },
  PipeIdLookupReviewerFieldId: {
    code: 'pipe-id-lookup/reviewer-field-id',
    explanation:
      'O ID do campo do revisor fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o campo foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do campo do revisor não foi encontrado.',
  },
  PipeIdLookupRoleId: {
    code: 'pipe-id-lookup/role-id',
    explanation:
      'O ID do papel (role) fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o papel foi removido ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID do cargo não foi encontrado.',
  },
  PipeIdLookupStageId: {
    code: 'pipe-id-lookup/stage-id',
    explanation:
      'O ID da etapa fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a etapa foi removida ou se o ID está incorreto.',
    localizations: {},
    message: 'O ID da etapa não foi encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'pipe-id-lookup'>;
